/**
 * ĐẨY FILE VIDEO THẲNG LÊN YOUTUBE (06/10/2026) theo giao thức «tải lên tiếp tục được» của Google.
 *
 * Máy chủ của app chỉ mở phiên (có tiêu đề · mô tả · tag · giờ hẹn) và trả địa chỉ phiên; byte của file đi từ
 * máy người dùng thẳng vào Google — file 6–18 GB không qua máy chủ, không qua lớp Bảo vệ DNS chậm. Google gắn
 * phiên với origin của trang web nên trình duyệt PUT vào được (CORS), và địa chỉ phiên tự nó là «chìa khoá»:
 * không cần mã truy cập ở phía trình duyệt.
 *
 * Cách đi (tài liệu «Resumable uploads» của YouTube và mẫu `cors_upload.js` của Google):
 *   1. PUT cả file (từ byte `offset`) vào địa chỉ phiên, `Content-Range` khi gửi tiếp;
 *   2. Google trả 200/201 kèm JSON video (có `id`) khi xong;
 *   3. đứt mạng / lỗi 5xx thì hỏi Google đã nhận tới đâu (PUT rỗng, `Content-Range: bytes * / tổng`): 308 kèm
 *      `Range: bytes=0-N` → gửi tiếp từ N+1; 404 = phiên đã hết hạn, phải mở phiên mới.
 * Dùng XMLHttpRequest chứ không `fetch`: `fetch` không báo tiến độ ĐƯA LÊN.
 */
import { nextByteOfRange } from '@/domain/video-upload';

export interface UploadProgress {
  /** Byte đã lên tới (gồm phần Google đã nhận ở các lượt trước). */
  sent: number;
  total: number;
}

export interface UploadRunOptions {
  onProgress?: (progress: UploadProgress) => void;
  signal?: AbortSignal;
  /** Số lần thử lại tối đa khi đứt mạng / Google lỗi tạm (mặc định 12, chờ tăng dần tới 60 giây). */
  maxRetries?: number;
  /** Phiên cũ gửi dở (bấm «Dừng» rồi «Tải lên» lại): hỏi Google đã nhận tới đâu trước khi gửi. */
  resume?: boolean;
}

/** Phiên tải lên không còn (404 / 410): phải xin máy chủ mở phiên mới. */
export class UploadSessionLostError extends Error {
  constructor() {
    super('Phiên tải lên đã hết hạn — bấm «Thử lại» để mở phiên mới');
    this.name = 'UploadSessionLostError';
  }
}

const aborted = () => new DOMException('Đã huỷ', 'AbortError');

/** Tìm mã video trong câu trả lời cuối của Google. */
function videoIdOf(text: string): string {
  try {
    const body = JSON.parse(text) as { id?: unknown };
    return typeof body.id === 'string' ? body.id : '';
  } catch {
    return '';
  }
}

interface Attempt {
  status: number;
  responseText: string;
  range: string | null;
}

/** Một lượt PUT: phần file từ `offset`, hoặc rỗng để hỏi tiến độ (`query`). Hỏng mạng thì ném lỗi. */
function put(
  url: string,
  file: File,
  offset: number,
  query: boolean,
  options: UploadRunOptions,
): Promise<Attempt> {
  return new Promise<Attempt>((resolve, reject) => {
    if (options.signal?.aborted) return reject(aborted());
    const request = new XMLHttpRequest();
    request.open('PUT', url);
    if (query) {
      request.setRequestHeader('Content-Range', `bytes */${file.size}`);
    } else {
      request.setRequestHeader('Content-Type', file.type || 'video/*');
      if (offset > 0) {
        request.setRequestHeader('Content-Range', `bytes ${offset}-${file.size - 1}/${file.size}`);
      }
      request.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          options.onProgress?.({ sent: offset + event.loaded, total: file.size });
        }
      };
    }
    request.onload = () =>
      resolve({
        status: request.status,
        responseText: request.responseText,
        range: request.getResponseHeader('Range'),
      });
    request.onerror = () => reject(new Error('Mất kết nối giữa chừng'));
    request.ontimeout = () => reject(new Error('Google không trả lời'));
    request.onabort = () => reject(aborted());
    options.signal?.addEventListener('abort', () => request.abort(), { once: true });
    request.send(query ? null : offset > 0 ? file.slice(offset) : file);
  });
}

const wait = (ms: number, signal?: AbortSignal) =>
  new Promise<void>((resolve, reject) => {
    if (signal?.aborted) return reject(aborted());
    const timer = setTimeout(resolve, ms);
    signal?.addEventListener(
      'abort',
      () => {
        clearTimeout(timer);
        reject(aborted());
      },
      { once: true },
    );
  });

/**
 * Đẩy `file` vào phiên `url`; trả mã video khi Google nhận xong. Tự gửi tiếp khi đứt mạng. Lỗi 4xx (trừ 404
 * và 408) là lỗi thật của phiên — không thử lại.
 */
export async function uploadToYoutube(
  url: string,
  file: File,
  options: UploadRunOptions = {},
): Promise<string> {
  const maxRetries = options.maxRetries ?? 12;
  let offset = 0;
  let retries = 0;
  let query = options.resume ?? false;
  for (;;) {
    let attempt: Attempt;
    try {
      attempt = await put(url, file, offset, query, options);
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') throw error;
      if (retries >= maxRetries) throw error;
      retries += 1;
      await wait(Math.min(60_000, 1000 * 2 ** retries), options.signal);
      query = true;
      continue;
    }
    if (attempt.status === 200 || attempt.status === 201) {
      options.onProgress?.({ sent: file.size, total: file.size });
      const id = videoIdOf(attempt.responseText);
      if (!id) throw new Error('Google nhận file nhưng không trả mã video');
      return id;
    }
    if (attempt.status === 308) {
      // Google báo đã nhận tới đâu (thiếu header thì gửi lại từ đầu — chậm nhưng đúng).
      offset = nextByteOfRange(attempt.range);
      query = false;
      retries = 0;
      continue;
    }
    // 404 / 410 (08/10/2026, chủ dự án báo «Google từ chối phiên tải lên (mã 410)»): phiên không còn — Google huỷ
    // phiên sau khi hết hạn hay hỏng; chỉ còn cách mở phiên mới.
    if (attempt.status === 404 || attempt.status === 410) throw new UploadSessionLostError();
    if (attempt.status >= 500 || attempt.status === 408 || attempt.status === 429) {
      if (retries >= maxRetries) throw new Error(`Google lỗi tạm (mã ${attempt.status})`);
      retries += 1;
      await wait(Math.min(60_000, 1000 * 2 ** retries), options.signal);
      query = true;
      continue;
    }
    throw new Error(`Google từ chối phiên tải lên (mã ${attempt.status})`);
  }
}
